// /settings/branding - Branding Setup (PAGES.md §3.79)
//
// Logo, brand color, receipt header/footer, invoice template, live preview.
// SHARED vertical. OWNER+ (50). Traceability: FR-COMM-006

import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Input,
  TextArea,
  Select,
  Toggle,
  FileUpload,
  Skeleton,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import type { TaxRegimePresentation } from '@bbpos/ui';
import { amountToMajor, getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import { useAuthStore } from '@/stores/use-auth-store';
import { tenantService } from '@/services/tenant.service';
import { ApiError } from '@/lib/api-client';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { formatPaise } from '@/utils/currency';
import { GLASS_FORM, GLASS, cn } from '@/constants/design-tokens';
import { uploadTenantLogo } from './logo-upload';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { SaveStatus } from '@/components/SaveStatus';

const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;

const INVOICE_TEMPLATE_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'compact', label: 'Compact' },
  { value: 'detailed', label: 'Detailed' },
];

function ReceiptPreview({
  businessName,
  brandColor,
  receiptHeader,
  receiptFooter,
  gstin,
  showLogo,
  showGstin,
  logoUrl,
  tax,
  currency,
}: {
  businessName: string;
  brandColor: string;
  receiptHeader: string;
  receiptFooter: string;
  gstin: string;
  showLogo: boolean;
  showGstin: boolean;
  logoUrl: string;
  tax: TaxRegimePresentation;
  currency: CurrencyCode;
}) {
  const validColor = HEX_COLOR_REGEX.test(brandColor) ? brandColor : '#3B82F6';
  // Sample amounts are whole major units scaled to the tenant's minor units,
  // so an OMR tenant sees 3 decimals and an INR tenant keeps 2 (T2-001).
  const minor = (major: number) => major * getSubunitMultiplier(currency);
  const amount = (major: number) => amountToMajor(minor(major), currency);

  return (
    <div className={cn(GLASS, 'p-5')} aria-label="Receipt preview with current settings">
      <h3 className="text-xs font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-4">
        Receipt Preview
      </h3>
      <div className="bg-white dark:bg-neutral-950 rounded-xl p-5 shadow-inner max-w-[280px] mx-auto font-mono text-xs text-neutral-800 dark:text-neutral-200 space-y-2">
        <div className="text-center space-y-1">
          {showLogo && (
            <div className="flex justify-center mb-2">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="h-10 w-auto object-contain" />
              ) : (
                <div
                  className="h-10 w-10 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                  style={{ backgroundColor: validColor }}
                >
                  {businessName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          )}
          <p className="font-bold text-sm" style={{ color: validColor }}>
            {receiptHeader || businessName || 'Your Business'}
          </p>
          {showGstin && gstin && tax.taxIdLabel && (
            <p className="text-[10px] text-neutral-500 tracking-wide font-mono">{tax.taxIdLabel}: {gstin}</p>
          )}
        </div>

        <div className="text-center text-neutral-300 dark:text-neutral-600 tracking-widest">- - - - - - - - - - - -</div>

        <div className="space-y-1">
          <div className="flex justify-between">
            <span>Chicken Biryani x 2</span>
            <span className="font-mono tabular-nums">{amount(500)}</span>
          </div>
          <div className="flex justify-between">
            <span>Butter Naan x 4</span>
            <span className="font-mono tabular-nums">{amount(200)}</span>
          </div>
          <div className="flex justify-between">
            <span>Masala Chai x 2</span>
            <span className="font-mono tabular-nums">{amount(60)}</span>
          </div>
        </div>

        <div className="text-center text-neutral-300 dark:text-neutral-600 tracking-widest">- - - - - - - - - - - -</div>

        <div className="space-y-0.5 text-[10px] text-neutral-500 dark:text-neutral-400">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span className="font-mono tabular-nums">{amount(760)}</span>
          </div>
          {tax.splitLabels ? (
            <>
              <div className="flex justify-between">
                <span>CGST (2.5%)</span>
                <span className="font-mono tabular-nums">{amount(19)}</span>
              </div>
              <div className="flex justify-between">
                <span>SGST (2.5%)</span>
                <span className="font-mono tabular-nums">{amount(19)}</span>
              </div>
            </>
          ) : (
            <div className="flex justify-between">
              <span>{tax.taxName}</span>
              <span className="font-mono tabular-nums">{amount(38)}</span>
            </div>
          )}
        </div>

        <div className="text-center text-neutral-300 dark:text-neutral-600 tracking-widest">- - - - - - - - - - - -</div>

        <div className="flex justify-between font-bold text-sm">
          <span>TOTAL</span>
          <span className="font-mono tabular-nums" style={{ color: validColor }}>
            {formatPaise(minor(798), currency)}
          </span>
        </div>

        <div className="text-center text-neutral-300 dark:text-neutral-600 tracking-widest">- - - - - - - - - - - -</div>

        <p className="text-center text-[10px] text-neutral-400 dark:text-neutral-500">
          {receiptFooter || 'Thank you for your visit!'}
        </p>
      </div>
    </div>
  );
}

function BrandingContent() {
  const { toast } = useToast();
  const profile = useAuthStore((s) => s.profile);
  const tenantId = profile?.tenant_id ?? '';
  // Receipt tax-id / tax-line vocabulary derives from the tenant's immutable
  // currency. INR → GST_INDIA: `GSTIN` + split CGST/SGST rows (byte-identical).
  // GCC → `TRN`/`VATIN`/… + single VAT line; Qatar/Kuwait have no statutory id.
  const tax = useTaxRegime();
  const currency = useTenantCurrency();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isLogoUploading, setIsLogoUploading] = useState(false);

  const [brandColor, setBrandColor] = useState('#3B82F6');
  const [logoUrl, setLogoUrl] = useState('');
  const [receiptHeader, setReceiptHeader] = useState('');
  const [receiptFooter, setReceiptFooter] = useState('Thank you for your visit!');
  const [invoiceTemplate, setInvoiceTemplate] = useState('default');
  const [showLogoOnReceipt, setShowLogoOnReceipt] = useState(true);
  const [showGstinOnReceipt, setShowGstinOnReceipt] = useState(true);

  const businessName = profile?.tenant?.business_name ?? 'Your Business';
  const [gstin, setGstin] = useState('');
  // Serialized form as last loaded/saved; empty until the first load lands.
  const initialRef = useRef<string>('');

  useEffect(() => {
    document.title = 'Branding - Settings - Orio360 Admin';
  }, []);

  useEffect(() => {
    if (!tenantId) return;

    setIsLoading(true);
    tenantService
      .getById(tenantId)
      .then((data) => {
        const tenantData = data as unknown as Record<string, unknown>;
        // Field names follow UpdateBrandingDto / the tenants columns exactly.
        // The page used to read and write brand_color / receipt_header, which
        // the API never knew, so those values reverted on reload (QA 3.7.11).
        const loaded = {
          primary_color: String(tenantData.primary_color ?? '#3B82F6'),
          logo_url: String(tenantData.logo_url ?? ''),
          invoice_header: String(tenantData.invoice_header ?? data.business_name ?? ''),
          receipt_footer: String(tenantData.receipt_footer ?? 'Thank you for your visit!'),
          invoice_template: String(tenantData.invoice_template ?? 'default'),
          show_logo_on_receipt: tenantData.show_logo_on_receipt !== false,
          show_gstin_on_receipt: tenantData.show_gstin_on_receipt !== false,
        };
        setBrandColor(loaded.primary_color);
        setLogoUrl(loaded.logo_url);
        setReceiptHeader(loaded.invoice_header);
        setReceiptFooter(loaded.receipt_footer);
        setInvoiceTemplate(loaded.invoice_template);
        setShowLogoOnReceipt(loaded.show_logo_on_receipt);
        setShowGstinOnReceipt(loaded.show_gstin_on_receipt);
        setGstin(String(tenantData.gstin ?? data.gstin ?? ''));
        initialRef.current = JSON.stringify(loaded);
      })
      .catch(() => {
        toast({ title: 'Failed to load branding settings', variant: 'error' });
      })
      .finally(() => setIsLoading(false));
  }, [tenantId, toast]);

  // Same shape and key order as `loaded` above, so a pristine form compares equal.
  const currentSnapshot = JSON.stringify({
    primary_color: brandColor,
    logo_url: logoUrl,
    invoice_header: receiptHeader,
    receipt_footer: receiptFooter,
    invoice_template: invoiceTemplate,
    show_logo_on_receipt: showLogoOnReceipt,
    show_gstin_on_receipt: showGstinOnReceipt,
  });
  const isDirty = initialRef.current !== '' && currentSnapshot !== initialRef.current;

  const handleLogoUpload = useCallback(
    async (files: File[]) => {
      if (!files.length) {
        setLogoUrl('');
        return;
      }

      const file = files[0];
      setIsLogoUploading(true);

      try {
        // Was a presign + unchecked PUT, which shared bug #270 with the other
        // logo path: an HTTP 403/500 resolves normally, so a failed upload
        // still set a logo_url pointing at nothing.
        const fileUrl = await uploadTenantLogo(file);

        setLogoUrl(fileUrl);
        // Only local form state changes here; nothing is persisted until Save
        // Branding, so the toast must not read as a completed save (QA 3.7.12).
        toast({ title: 'Logo ready — click Save Branding to apply', variant: 'success' });
      } catch {
        toast({ title: 'Logo upload failed', variant: 'error' });
      } finally {
        setIsLogoUploading(false);
      }
    },
    [toast],
  );

  const handleSave = useCallback(async () => {
    if (!tenantId) return;
    if (brandColor && !HEX_COLOR_REGEX.test(brandColor)) {
      toast({ title: 'Invalid color format (use #RRGGBB)', variant: 'error' });
      return;
    }

    setIsSaving(true);
    try {
      await tenantService.updateBranding(tenantId, {
        primary_color: brandColor || undefined,
        logo_url: logoUrl || undefined,
        invoice_header: receiptHeader || undefined,
        receipt_footer: receiptFooter || undefined,
        invoice_template: invoiceTemplate || undefined,
        show_logo_on_receipt: showLogoOnReceipt,
        show_gstin_on_receipt: showGstinOnReceipt,
      });
      toast({ title: 'Branding settings saved', variant: 'success' });
      initialRef.current = currentSnapshot;
    } catch (err) {
      toast({
        title: err instanceof ApiError ? err.message : 'Failed to save',
        variant: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    tenantId,
    brandColor,
    logoUrl,
    receiptHeader,
    receiptFooter,
    invoiceTemplate,
    showLogoOnReceipt,
    showGstinOnReceipt,
    currentSnapshot,
    toast,
  ]);

  // Keyboard: Ctrl+S — mirror the Save button's guard (no-op when pristine or already saving)
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        if (!isDirty || isSaving) return;
        handleSave();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, isDirty, isSaving]);

  // Unsaved-changes guard: warn before the tab is closed or reloaded, or an
  // in-app link is followed, while the form (including a just-uploaded,
  // not-yet-saved logo) is dirty.
  const { dialog: leaveDialog } = useUnsavedChangesGuard(isDirty);

  return (
    <PageContainer
      title="Branding"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Settings', href: '/settings' },
        { label: 'Branding' },
      ]}
    >
      <div className="animate-fade-in">
        <Link
          to="/settings"
          className="text-sm text-brand-600 dark:text-brand-400 hover:underline mb-4 inline-block"
        >
          Back to Settings
        </Link>

        <div className="bg-gradient-to-r from-brand-600 via-indigo-600 to-violet-600 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(99,102,241,0.45)] mb-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
          <div className="relative">
            <h1 className="text-2xl font-bold text-white">Branding</h1>
            <p className="text-neutral-300 mt-1">Customize your business appearance on receipts and invoices</p>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton variant="rect" height={500} className="rounded-2xl" />
            <Skeleton variant="rect" height={500} className="rounded-2xl" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className={cn(GLASS_FORM, 'p-5 sm:p-6 animate-slide-up')}>
              <h2 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider mb-4">
                Brand Settings
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-2 block">
                    Brand Logo
                  </label>
                  <FileUpload
                    variant="dropzone"
                    accept="image/png,image/jpeg,image/webp"
                    maxSize={5 * 1024 * 1024}
                    preview={logoUrl || null}
                    onUpload={handleLogoUpload}
                    loading={isLogoUploading}
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
                    Brand Color
                  </label>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg shadow-sm flex-shrink-0 transition-colors duration-200"
                      style={{ backgroundColor: HEX_COLOR_REGEX.test(brandColor) ? brandColor : '#3B82F6' }}
                      aria-label="Brand primary color"
                    />
                    <Input
                      value={brandColor}
                      onChange={setBrandColor}
                      placeholder="#3B82F6"
                      className="font-mono"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
                    Receipt Header Text
                  </label>
                  <TextArea
                    value={receiptHeader}
                    onChange={setReceiptHeader}
                    placeholder="Business name or custom header"
                    rows={2}
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
                    Receipt Footer Text
                  </label>
                  <TextArea
                    value={receiptFooter}
                    onChange={setReceiptFooter}
                    placeholder="Thank you for your visit!"
                    rows={2}
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
                    Invoice Template
                  </label>
                  <Select
                    options={INVOICE_TEMPLATE_OPTIONS}
                    value={invoiceTemplate}
                    onChange={(value) => setInvoiceTemplate(typeof value === 'string' ? value : value[0] ?? 'default')}
                  />
                </div>

                <div className="space-y-3 pt-2">
                  <Toggle
                    checked={showLogoOnReceipt}
                    onChange={setShowLogoOnReceipt}
                    label="Show Logo on Receipt"
                    description="Display your business logo at the top of receipts"
                    className="w-full"
                  />

                  {tax.taxIdLabel && (
                    <Toggle
                      checked={showGstinOnReceipt}
                      onChange={setShowGstinOnReceipt}
                      label={`Show ${tax.taxIdLabel} on Receipt`}
                      description={`Display your ${tax.taxName} number on receipts and invoices`}
                      className="w-full"
                    />
                  )}
                </div>

                <div className="flex items-center justify-end gap-3 pt-4">
                  <SaveStatus dirty={isDirty} />
                  <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving || !isDirty}>
                    {isSaving ? 'Saving...' : 'Save Branding'}
                  </Button>
                </div>
              </div>
            </div>

            <div className="animate-slide-up" style={{ animationDelay: '80ms' }}>
              <ReceiptPreview
                businessName={businessName}
                brandColor={brandColor}
                receiptHeader={receiptHeader}
                receiptFooter={receiptFooter}
                gstin={gstin}
                showLogo={showLogoOnReceipt}
                showGstin={showGstinOnReceipt}
                logoUrl={logoUrl}
                tax={tax}
                currency={currency}
              />
            </div>
          </div>
        )}
      </div>
      {leaveDialog}
    </PageContainer>
  );
}

export function BrandingPage() {
  return (
    <ErrorBoundary>
      <BrandingContent />
    </ErrorBoundary>
  );
}
